import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { History } from "lucide-react";
import { cn, formatINR } from "@/lib/utils";
import {
  useEntrySuggestions,
  type SuggestionTemplate,
  type SuggestionType,
} from "@/lib/hooks/use-entry-suggestions";

interface DescriptionTypeaheadProps {
  type: SuggestionType;
  value: string;
  onChange: (value: string) => void;
  /** Fired when the user picks a template — fill category/amount/account etc. */
  onSelectTemplate: (t: SuggestionTemplate) => void;
  placeholder?: string;
  /** Render a 2-row textarea (forms) instead of a single-line input (QuickAdd). */
  multiline?: boolean;
  className?: string;
  inputClassName?: string;
}

/**
 * Description input with a history-based typeahead dropdown.
 *
 * Shared by QuickAddSheet, IncomeForm and ExpenseForm so template-selection
 * behaviour stays identical on every surface. Options use onMouseDown (not
 * onClick) so selection wins the race against the input's blur.
 */
export function DescriptionTypeahead({
  type,
  value,
  onChange,
  onSelectTemplate,
  placeholder = "Description...",
  multiline = false,
  className,
  inputClassName,
}: DescriptionTypeaheadProps) {
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const q = value.trim();
  const { data: suggestions = [] } = useEntrySuggestions(
    type,
    q,
    focused && q.length > 0,
  );

  const open = focused && !dismissed && q.length > 0 && suggestions.length > 0;

  const sharedInputProps = {
    value,
    placeholder,
    onFocus: () => {
      setFocused(true);
      setDismissed(false);
    },
    onBlur: () => setFocused(false),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        e.stopPropagation();
        setDismissed(true);
      }
    },
  };

  const inputClasses = cn(
    "w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none",
    inputClassName,
  );

  return (
    <div className={cn("relative", className)}>
      {multiline ? (
        <textarea
          {...sharedInputProps}
          rows={2}
          onChange={(e) => {
            onChange(e.target.value);
            setDismissed(false);
          }}
          className={inputClasses}
        />
      ) : (
        <input
          {...sharedInputProps}
          type="text"
          autoComplete="off"
          onChange={(e) => {
            onChange(e.target.value);
            setDismissed(false);
          }}
          className={inputClasses}
        />
      )}

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={shouldReduceMotion ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute left-0 right-0 top-full z-40 mt-1 max-h-56 overflow-y-auto rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] py-1 shadow-[var(--cp-shadow-lg)]"
            role="listbox"
            aria-label="Suggestions from your history"
          >
            {suggestions.map((s) => (
              <li key={`${s.categoryId}-${s.description}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep input focus; beat blur
                    onSelectTemplate(s);
                    setDismissed(true);
                  }}
                  className="flex min-h-10 w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--cp-surface-2)]"
                >
                  <History className="h-3.5 w-3.5 shrink-0 text-[var(--cp-ink-3)]" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-[var(--cp-ink)]">
                      {s.description}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-[var(--cp-ink-3)]">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: s.category.color ?? "#94a3b8" }}
                        aria-hidden
                      />
                      <span className="truncate">{s.category.name}</span>
                      <span aria-hidden>·</span>
                      <span>{s.count}×</span>
                    </span>
                  </span>
                  <span className="cp-num shrink-0 text-sm font-semibold text-[var(--cp-ink-2)]">
                    {formatINR(s.amountPaise)}
                  </span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
